import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Building2, ChevronDown, Check, Star } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export default function StrutturaSwitcher({ variant = "sidebar", collapsed = false }) {
  const { strutture, activeStruttura, switchStruttura } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [switching, setSwitching] = useState(false);

  if (!strutture || strutture.length <= 1) return null;

  const handleSwitch = async (strutturaId) => {
    if (strutturaId === activeStruttura?.id || switching) return;
    setSwitching(true);
    try {
      await switchStruttura(strutturaId);
      const target = strutture.find(s => s.id === strutturaId);
      toast.success(`Struttura cambiata: ${target?.name || "Struttura"}`);
      const isDashboard = location.pathname === "/" || location.pathname === "/dashboard";
      if (!isDashboard) {
        navigate("/", { replace: true });
      }
    } catch {
      toast.error("Errore nel cambio struttura");
    } finally {
      setSwitching(false);
    }
  };

  const isSidebar = variant === "sidebar";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={`flex w-full items-center text-left transition-colors rounded-xl ${
            collapsed
              ? "h-10 justify-center text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 shadow-sm"
              : isSidebar
                ? "gap-2 px-3 py-2.5 text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 shadow-sm"
                : "gap-2 px-3 py-2 text-slate-700 hover:bg-slate-100 border border-slate-200"
          } ${switching ? "opacity-50 pointer-events-none" : ""}`}
          disabled={switching}
          title={collapsed ? (activeStruttura?.name || "Struttura") : undefined}
        >
          <Building2 className="w-4 h-4 flex-shrink-0 text-slate-500" />
          {!collapsed ? (
            <>
              <span className="text-sm font-semibold truncate flex-1">
                {activeStruttura?.name || "Struttura"}
              </span>
              <ChevronDown className="w-4 h-4 flex-shrink-0 text-slate-500" />
            </>
          ) : (
            <span className="sr-only">{activeStruttura?.name || "Struttura"}</span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side={collapsed ? "right" : "bottom"} className="w-64">
        <div className="px-3 py-2 border-b">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
            Cambia struttura
          </p>
        </div>
        {strutture.map((s) => (
          <DropdownMenuItem
            key={s.id}
            onClick={() => handleSwitch(s.id)}
            className="flex items-center gap-2 py-2.5"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-medium truncate">{s.name}</span>
                {s.is_primary && (
                  <Star className="w-3 h-3 text-amber-500 flex-shrink-0" />
                )}
              </div>
              {s.tipoStruttura && (
                <span className="text-xs text-slate-500">{s.tipoStruttura}</span>
              )}
            </div>
            {s.id === activeStruttura?.id && (
              <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
